/**
 * Séquence 5 — Sensibilisation aux risques (1 h min)
 *
 * Séquence de clôture, en présence d'un parent/représentant légal si mineur.
 * L'enseignant traite au moins 4 des 7 thèmes officiels, selon le profil.
 *
 * On garde EXACTEMENT la trame pédagogique du module cyclo (accroche + questions
 * + à retenir + scénarios) mais TOUT le contenu est adapté à la voiturette.
 */
export const sequence5 = {
  officiel: {
    parentPresent: 'Présence obligatoire d’un parent ou représentant légal si l’élève est mineur.',
    regle: "L'enseignant traite AU MOINS 4 des 7 thèmes officiels, choisis en fonction du profil de l'élève.",
    themes: [
      { num: 1, key: 'risques-specifiques', label: 'Risques spécifiques aux quadricycles légers', desc: 'Accidents caractéristiques — vulnerabilité en cas de choc.' },
      { num: 2, key: 'entretien',            label: "Défaut d'entretien",                          desc: "Conséquences sur la sécurité et l'environnement." },
      { num: 3, key: 'debridage',            label: 'Débridage · Réglementation',                  desc: 'Conséquences pénales, sécurité, environnement.' },
      { num: 4, key: 'psychoactifs',         label: 'Produits psychoactifs et conduite',           desc: 'Alcool, drogues, médicaments.' },
      { num: 5, key: 'vitesse',              label: 'Vitesse et conséquences',                     desc: 'Équilibre · adhérence · force centrifuge · freinage.' },
      { num: 6, key: 'vulnerables',          label: 'Usagers vulnérables',                         desc: 'Piétons · cyclistes · motocyclistes.' },
      { num: 7, key: 'pairs',                label: 'Influence et pression des pairs',             desc: 'Prise de risque en groupe, réseaux sociaux.' },
    ],
  },
  // Contenu enrichi — reprend la trame pédagogique du module sensibilisation
  // cyclomoteur, MAIS adapté en profondeur au quadricycle léger.
  enrichi: {
    slug: 'permis-am-voiturette-sensibilisation',
    title: 'Séquence 5 — Sensibilisation Voiturette',
    subtitle: 'Comprendre les risques spécifiques à la voiturette',
    duree: '1 h · Présence parentale obligatoire (mineur)',
    mantra: "UNE VOITURETTE, C'EST UNE VOITURE LENTE DANS UN MONDE RAPIDE — TON ANTICIPATION EST TA MEILLEURE PROTECTION.",
    intro:
      "Tu sais maintenant démarrer, t'insérer, ralentir et manœuvrer ta voiturette. Mais savoir conduire ne suffit pas. Pendant cette heure, on parle des situations que TU rencontreras vraiment : automobilistes pressés qui te collent, tentation du débridage, pression des copains, alcool en soirée. L'objectif n'est pas de te faire peur : c'est de te donner des repères AVANT que tu sois seul face à ces situations.",
    sections: [
      {
        num: '01',
        kicker: 'Comprendre pour éviter',
        title: "Pourquoi parler des accidents en voiturette ?",
        lead: "On ne cherche pas à dramatiser. On cherche à comprendre comment un accident se construit dans TON contexte : véhicule lent, jeune conducteur, environnement routier normal.",
        type: 'list',
        list: [
          { label: 'COMMENT ?',    desc: "Comment l'accident s'est-il construit ?" },
          { label: 'INDICES',      desc: "Quels indices étaient présents avant ?" },
          { label: 'DÉCISION',     desc: 'Quelle décision aurait pu changer la situation ?' },
          { label: 'ANTICIPATION', desc: "Qu'aurait pu anticiper le conducteur ?" },
        ],
        retenir: "COMPRENDRE COMMENT UN ACCIDENT SE CONSTRUIT, C'EST DÉJÀ APPRENDRE À L'ÉVITER.",
      },
      {
        num: '02',
        kicker: 'Vulnérabilité · Thème officiel n°1',
        title: 'La voiturette protège moins qu’une voiture',
        lead: "Malgré la carrosserie, la ceinture et le volant, la voiturette est plus légère, moins rigide et sans airbag. En cas de choc frontal même à basse vitesse, les conséquences peuvent être graves.",
        type: 'cards3',
        cards: [
          { title: 'STRUCTURE LÉGÈRE', desc: 'Châssis moins résistant qu’une voiture classique.' },
          { title: 'PAS D’AIRBAG',      desc: 'La plupart des modèles n’en ont pas.' },
          { title: 'CEINTURE = SURVIE', desc: 'Elle est TON principal équipement passif.' },
        ],
        retenir: 'LA CEINTURE EST OBLIGATOIRE ET NON NÉGOCIABLE, POUR TOI ET POUR TON PASSAGER.',
      },
      {
        num: '03',
        kicker: 'Vitesse · Thème officiel n°5',
        title: "45 km/h, ce n'est pas rien",
        lead: 'Une voiturette à 45 km/h dans un choc frontal équivaut à une chute du 3e étage. Ta vitesse plafonnée ne fait pas de toi un « véhicule inoffensif ».',
        type: 'list',
        list: [
          { label: 'DISTANCE ARRÊT',  desc: "À 45 km/h sec, il te faut ~14 m pour t'arrêter. Mouillé : ~20 m." },
          { label: 'FORCE CENTRIFUGE', desc: 'En virage, la voiturette peut basculer si tu abordes trop vite.' },
          { label: 'ADHÉRENCE',       desc: 'Sur pluie ou feuilles : distances doublées.' },
          { label: 'FREINAGE',        desc: "Débrayage + frein + trajectoire : ne freine pas dans un virage." },
        ],
        retenir: 'MÊME LENTE, LA VOITURETTE OBÉIT AUX LOIS DE LA PHYSIQUE. RESPECTE LES DISTANCES.',
      },
      {
        num: '04',
        kicker: 'Débridage · Thème officiel n°3',
        title: 'Débrider ta voiturette = délit',
        lead: "Faire dépasser 45 km/h à ta voiturette est illégal. Au-delà, on parle d'un autre véhicule pour lequel il faut un autre permis. La détection est facile pour les forces de l'ordre (ordinateur de bord).",
        type: 'list',
        list: [
          { label: 'AMENDE',       desc: "Jusqu'à 1 500 €. Confiscation possible du véhicule." },
          { label: 'ASSURANCE',    desc: "L'assurance ne couvre plus rien en cas d'accident." },
          { label: 'RESPONSABILITÉ', desc: 'En cas de blessé : poursuites pénales.' },
          { label: 'SÉCURITÉ',     desc: 'La voiturette n’est pas conçue pour rouler plus vite : freinage, direction, tenue de route inadaptés.' },
        ],
        retenir: 'UN DÉBRIDAGE = UN CASIER JUDICIAIRE POSSIBLE + AUCUNE ASSURANCE.',
      },
      {
        num: '05',
        kicker: 'Alcool & produits · Thème officiel n°4',
        title: 'Alcool, drogues, médicaments : même en voiturette',
        lead: "La législation ne fait AUCUNE distinction entre voiture et voiturette. 0,5 g/L max (0,2 g/L pour les jeunes conducteurs).",
        type: 'list',
        list: [
          { label: 'JEUNE PERMIS',  desc: '0,2 g/L pour les 3 premières années de conduite (permis probatoire).' },
          { label: 'CANNABIS',      desc: 'Détecté des jours après consommation. Tolérance zéro.' },
          { label: 'MÉDICAMENTS',   desc: "Certains rendent la conduite dangereuse (pictogramme sur la boîte)." },
          { label: 'FATIGUE',       desc: 'Effet similaire à l’alcool : temps de réaction doublé.' },
        ],
        retenir: "LE MEILLEUR TAUX D'ALCOOL AU VOLANT = ZÉRO.",
      },
      {
        num: '06',
        kicker: 'Autres usagers · Thème officiel n°6',
        title: 'Piétons, cyclistes, motos : tu es responsable d’eux',
        lead: "En agglomération, l'écrasante majorité de tes trajets croisés se fera avec des usagers plus vulnérables que toi. Même lents, même petits, ils ont légalement priorité sur toi dans de nombreux cas.",
        type: 'cards3',
        cards: [
          { title: 'PIÉTON', desc: 'Priorité absolue au passage protégé. Toujours céder le pas.' },
          { title: 'CYCLISTE', desc: 'Dépasse-le à 1 m minimum en ville, 1,50 m hors agglo.' },
          { title: 'MOTO', desc: 'Peut sortir de nulle part entre deux voies. Vérifie tes angles morts.' },
        ],
        retenir: 'ANTICIPER LES USAGERS VULNÉRABLES, C’EST LE B-A-BA D’UN CONDUCTEUR RESPONSABLE.',
      },
      {
        num: '07',
        kicker: 'Influence des pairs · Thème officiel n°7',
        title: 'Ce que pensent les copains ne fait pas ta sécurité',
        lead: "Un ado sur deux dit avoir déjà pris un risque en voiturette « parce que ses potes le poussaient » : débrider, transporter 3 personnes, faire du drift, prendre le téléphone. Chaque risque pris crée un antécédent.",
        type: 'scenarios',
        scenarios: [
          {
            id: 'debridage-pote',
            title: 'Ton pote te propose de débrider',
            question: "Il te dit « tout le monde le fait, c'est pas grave, tu vas juste à 50 ». Tu fais quoi ?",
            reponses: [
              { txt: "J’accepte, après tout c'est ma voiturette",  bon: false, feedback: "Non : c'est un délit + ton assurance saute." },
              { txt: "Je refuse et j'explique pourquoi",           bon: true,  feedback: "Bien joué : tu protèges ton casier ET ton portefeuille." },
              { txt: "Je dis oui pour ne pas passer pour un lourd", bon: false, feedback: "Rappelle-toi : c'est TOI qui prends l'amende, pas lui." },
            ],
          },
          {
            id: 'passagers',
            title: 'Trois copains veulent monter avec toi',
            question: "Ta voiturette est homologuée pour 2 places. Un pote insiste pour venir en 3e.",
            reponses: [
              { txt: "J'accepte s'il tient sur les genoux", bon: false, feedback: "Illegal + très dangereux : voiturette pas conçue pour 3 personnes." },
              { txt: "Je refuse, je fais 2 trajets si besoin", bon: true, feedback: "C’est le bon réflexe. Un pote qui insiste n'est pas un pote." },
              { txt: "J'accepte, on tente le coup", bon: false, feedback: "Amende + points perdus + gros risque en cas d'accident." },
            ],
          },
          {
            id: 'alcool-soiree',
            title: 'Soirée chez un pote, tu as bu 2 verres',
            question: "Il est 2h du matin, tu dois rentrer. Tu fais quoi ?",
            reponses: [
              { txt: "Je rentre doucement, j'ai pas bu tant que ça", bon: false, feedback: "Avec 2 verres tu es probablement au-dessus de 0,2 g/L : perte du permis + amende." },
              { txt: "Je dors sur place ou j'appelle mes parents",   bon: true, feedback: "Le meilleur plan B. Personne ne t'en voudra." },
              { txt: "Je fais un test au bar", bon: false, feedback: "L'éthylotest n'est jamais 100% fiable et l'alcoolémie MONTE encore après le dernier verre." },
            ],
          },
        ],
        retenir: 'DIRE NON EN 5 SECONDES = ÉVITER 5 ANNÉES DE REGRETS.',
      },
      {
        num: '08',
        kicker: 'Entretien · Thème officiel n°2',
        title: 'Une voiturette mal entretenue = un danger permanent',
        lead: "Les points à vérifier régulièrement en voiturette — souvent oubliés parce qu'on la considère moins « sérieuse » qu'une voiture.",
        type: 'list',
        list: [
          { label: 'PNEUS',       desc: "Sculpture, usure, pression. Un pneu lisse = distance d'arrêt multipliée par 2." },
          { label: 'FREINS',      desc: 'Bruits inhabituels, pédale molle = contrôle immédiat.' },
          { label: 'ÉCLAIRAGE',    desc: "Feux, clignotants, stop : tester chaque jour au démarrage." },
          { label: 'DIRECTION',   desc: 'Volant qui vibre ou tire d’un côté = pas normal.' },
          { label: 'REVÉRIFIER',   desc: 'Après chaque long trajet ou choc, même minime.' },
        ],
        retenir: "C'EST LE PROPRIÉTAIRE (OU SES PARENTS) QUI RESTE RESPONSABLE DE L'ÉTAT DU VÉHICULE.",
      },
      {
        num: '09',
        kicker: 'Ton engagement',
        title: 'Mon plan B pour rester en sécurité',
        lead: "En bilan, tu remplis ta check-list personnelle : les 5 promesses que tu te fais avant de partir seul avec ta voiturette. À conserver dans le carnet.",
        type: 'checklist',
        checklist: [
          'Je mets la ceinture systématiquement, moi ET mon passager',
          'Je NE débride PAS ma voiturette',
          'Je NE bois JAMAIS avant de conduire, même un verre',
          "J'appelle mes parents ou je dors sur place si je suis pas sûr",
          'Je respecte les priorités et je cède le passage aux plus vulnérables',
        ],
        retenir: "UNE PROMESSE ÉCRITE ET SIGNÉE VAUT 100 FOIS UNE BONNE INTENTION.",
      },
    ],
  },
};

export default sequence5;
